import React from 'react'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'

import asanaLogo from 'assets/images/connectors/asana.svg'
import mondayLogo from 'assets/images/connectors/monday.svg'

interface WebCitationPillProps {
  url: string
  title: string
  source: string
}

// Source values the LLM emits for connector-backed citations. Web search
// uses an arbitrary domain string ("nytimes.com", "web") — only the connector
// sources below get a brand icon and a connector-specific hover label.
type ConnectorSource = 'monday' | 'asana'

const CONNECTOR_SOURCES: Record<
  ConnectorSource,
  { logo: string; label: string; hoverLabel: string }
> = {
  monday: { logo: mondayLogo, label: 'Monday', hoverLabel: 'Open in Monday' },
  asana: { logo: asanaLogo, label: 'Asana', hoverLabel: 'Open in Asana' },
}

const isConnectorSource = (s: string): s is ConnectorSource =>
  s === 'monday' || s === 'asana'

const WebCitationPill: React.FC<WebCitationPillProps> = ({
  url,
  title,
  source,
}) => {
  // `url` comes from model output (web-citation tags populated from web-search
  // results, which the ask_yogi_v2 system prompt explicitly treats as
  // untrusted). Only http(s) URLs become a navigable href — a
  // javascript:/data: URL must never be clickable. react-markdown's own URL
  // sanitization doesn't apply here because this pill renders its own anchor.
  const { href, domain } = React.useMemo<{
    href: string | undefined
    domain: string
  }>(() => {
    try {
      const parsed = new URL(url)
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return { href: url, domain: parsed.hostname }
      }
    } catch {
      /* invalid URL — render as non-navigable text */
    }
    return { href: undefined, domain: '' }
  }, [url])

  const connector = isConnectorSource(source) ? CONNECTOR_SOURCES[source] : null
  const displayLabel = connector ? connector.label : source
  const hoverLabel = connector?.hoverLabel

  const popoverContent = (
    <PopoverContent>
      <PopoverTitle>{title}</PopoverTitle>
      <PopoverSource>
        {connector ? (
          <img src={connector.logo} width={14} height={14} alt="" />
        ) : (
          domain && (
            <img
              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
              width={14}
              height={14}
              alt=""
            />
          )
        )}
        <span>{hoverLabel ?? source}</span>
      </PopoverSource>
    </PopoverContent>
  )

  return (
    <CitationPopover
      content={popoverContent}
      openOnHover
      nativeButton={false}
      initialFocus={false}
    >
      <Pill
        href={href}
        // Base UI stamps role="button" on a non-<button> trigger; a real link
        // must stay one.
        role={href ? 'link' : undefined}
        target={href ? '_blank' : undefined}
        rel={href ? 'noopener noreferrer' : undefined}
        onClick={(e) => e.stopPropagation()}
        aria-label={hoverLabel}
      >
        {connector && (
          <PillLogo src={connector.logo} alt="" width={12} height={12} />
        )}
        {displayLabel} ↗
      </Pill>
    </CitationPopover>
  )
}

export default WebCitationPill

const Pill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #eeeff3;
  border-radius: 100px;
  font-size: 12px;
  color: #555;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  vertical-align: middle;
  margin: 0 2px;

  &:hover {
    background: #e0e5ff;
    color: #337ea9;
    text-decoration: none;
  }

  &:visited {
    color: #555;
  }
`

const PillLogo = styled.img`
  display: inline-block;
  object-fit: contain;
  border-radius: 2px;
`

const PopoverContent = styled.div`
  padding: 16px 20px;
  max-width: 300px;
`

const PopoverTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  line-height: 1.4;
  color: #222;
`

const PopoverSource = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #888;
`

const CitationPopover = styled(Popover)`
  max-width: 320px;
`
